import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Banner, Button, Card, Field, Input } from "@magarac/ui";
import { isApiError } from "@magarac/shared/errors";
import { myTenantsSchema } from "@magarac/shared/schemas/me";
import { invitationPreviewSchema, type InvitationPreview } from "@magarac/shared/schemas/member";
import { LoadError } from "../components/LoadError";
import { api } from "../lib/api";
import { authClient } from "../lib/auth-client";
import { OFFLINE_MESSAGE } from "../lib/errors";

export const Route = createFileRoute("/accept-invite/$id")({
  component: AcceptInvitePage,
});

type View =
  | { kind: "checking" }
  | { kind: "loadFailed"; error: unknown }
  /** Expired, canceled, or unknown link. */
  | { kind: "invalid"; businessName: string | null; signedIn: boolean }
  /** Already accepted; signed-out visitors are sent to sign in. */
  | { kind: "used"; businessName: string }
  /** Signed in as someone other than the invited email. */
  | { kind: "wrongAccount"; invite: InvitationPreview; signedInAs: string }
  | { kind: "form"; invite: InvitationPreview; mode: "signup" | "signin" };

/**
 * ACC-5: landing page for invitation emails. The link is checked first (business, invited email,
 * still valid?) so nobody creates an account for a dead link or under a mistyped email. Every
 * outcome ends in a clear next step: accepted, sign in, ask for a new link, or switch account.
 */
function AcceptInvitePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [view, setView] = useState<View>({ kind: "checking" });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = useCallback(async (): Promise<string | null> => {
    const { error: err } = await authClient.organization
      .acceptInvitation({ invitationId: id })
      .catch(() => ({ error: { status: 0 } }));
    if (!err) return null;
    if (!err.status) return OFFLINE_MESSAGE;
    return err.status === 403
      ? "This invitation was sent to a different email address. Sign in with that email."
      : "This invitation is no longer valid. Ask the business owner to send a new one.";
  }, [id]);

  const check = useCallback(async () => {
    let invite: InvitationPreview | null = null;
    try {
      invite = await api.get(`/invitations/${id}`, invitationPreviewSchema);
    } catch (err) {
      if (!(isApiError(err) && err.status === 404)) {
        setView({ kind: "loadFailed", error: err });
        return;
      }
    }
    const { data: session } = await authClient.getSession();

    if (!invite || invite.state === "expired") {
      setView({ kind: "invalid", businessName: invite?.businessName ?? null, signedIn: !!session });
      return;
    }
    if (invite.state === "used") {
      if (session) {
        // Opened again after joining: just go in.
        const mine = await api.get("/me/tenants", myTenantsSchema).catch(() => null);
        if (mine && mine.tenants.length > 0) {
          await navigate({ to: "/" });
          return;
        }
      }
      setView({ kind: "used", businessName: invite.businessName });
      return;
    }
    if (session) {
      if (session.user.email.toLowerCase() !== invite.email.toLowerCase()) {
        setView({ kind: "wrongAccount", invite, signedInAs: session.user.email });
        return;
      }
      const failed = await accept();
      if (!failed) {
        await navigate({ to: "/" });
        return;
      }
      setError(failed);
      setView({ kind: "form", invite, mode: "signin" });
      return;
    }
    setView({ kind: "form", invite, mode: "signup" });
  }, [id, accept, navigate]);

  useEffect(() => {
    // Every state change in check() happens after an await (network), never synchronously.
    void (async () => {
      await Promise.resolve();
      await check();
    })();
  }, [check]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (view.kind !== "form") return;
    const { invite, mode } = view;
    setBusy(true);
    setError(null);
    const result = await (
      mode === "signup"
        ? authClient.signUp.email({ name, email: invite.email, password })
        : authClient.signIn.email({ email: invite.email, password })
    ).catch(() => ({ error: { status: 0 } }));
    // Two-step sign-in is on for this account: finish signing in on the sign-in page, which
    // then brings the person back here to accept.
    if (!result.error && result.data && "twoFactorRedirect" in result.data) {
      window.location.assign(`/login?redirect=${encodeURIComponent(`/accept-invite/${id}`)}`);
      return;
    }
    if (result.error) {
      setBusy(false);
      setError(
        !result.error.status
          ? OFFLINE_MESSAGE
          : result.error.status === 429
            ? "Too many tries. Wait a minute, then try again."
            : mode === "signup"
              ? 'We could not create the account. Use at least 10 characters for the password. If you already have an account, choose "I already have an account".'
              : "That password did not match. Try again, or sign in with an email link from the sign-in page.",
      );
      return;
    }
    const failed = await accept();
    setBusy(false);
    if (failed) {
      setError(failed);
      return;
    }
    await navigate({ to: "/" });
  };

  const switchAccount = async () => {
    if (view.kind !== "wrongAccount") return;
    await authClient.signOut();
    setView({ kind: "form", invite: view.invite, mode: "signup" });
  };

  if (view.kind === "checking") {
    return <main className="p-8 text-center text-muted">One moment…</main>;
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-4">
      {view.kind === "loadFailed" ? (
        <LoadError
          error={view.error}
          onRetry={() => {
            setView({ kind: "checking" });
            void check();
          }}
        />
      ) : null}

      {view.kind === "invalid" ? (
        <>
          <h1 className="mb-4 text-2xl font-semibold">This invitation has expired</h1>
          <Card>
            <p className="mb-4">
              {view.businessName
                ? `Ask the owner of ${view.businessName} to send you a new invitation.`
                : "Ask the business owner to send you a new invitation."}{" "}
              Invitations work for 7 days, and a new one replaces the old link.
            </p>
            {view.signedIn ? (
              <Button className="w-full" onClick={() => void navigate({ to: "/" })}>
                Go to J360
              </Button>
            ) : null}
          </Card>
        </>
      ) : null}

      {view.kind === "used" ? (
        <>
          <h1 className="mb-4 text-2xl font-semibold">You already joined {view.businessName}</h1>
          <Card>
            <p className="mb-4">This invitation has been accepted. Sign in to get to work.</p>
            <Link to="/login" className="block">
              <Button className="w-full">Sign in</Button>
            </Link>
          </Card>
        </>
      ) : null}

      {view.kind === "wrongAccount" ? (
        <>
          <h1 className="mb-4 text-2xl font-semibold">Join {view.invite.businessName}</h1>
          <Card>
            <p className="mb-4">
              This invitation is for <strong>{view.invite.email}</strong>, but you are signed in as{" "}
              <strong>{view.signedInAs}</strong>.
            </p>
            <div className="grid gap-2">
              <Button className="w-full" onClick={() => void switchAccount()}>
                Sign out and continue as {view.invite.email}
              </Button>
              <Button variant="secondary" onClick={() => void navigate({ to: "/" })}>
                Stay signed in as {view.signedInAs}
              </Button>
            </div>
          </Card>
        </>
      ) : null}

      {view.kind === "form" ? (
        <>
          <h1 className="mb-1 text-2xl font-semibold">Join {view.invite.businessName}</h1>
          <p className="mb-4 text-muted">
            {view.mode === "signup"
              ? "Create your account to accept the invitation."
              : "Sign in to accept the invitation."}
          </p>
          <Card>
            {error ? <Banner tone="danger">{error}</Banner> : null}
            <form onSubmit={(e) => void submit(e)} noValidate>
              {view.mode === "signup" ? (
                <Field id="name" label="Your name">
                  <Input
                    id="name"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Field>
              ) : null}
              <Field id="email" label="Email" hint="The invitation was sent to this address.">
                <Input id="email" type="email" value={view.invite.email} readOnly />
              </Field>
              <Field
                id="password"
                label="Password"
                hint={view.mode === "signup" ? "At least 10 characters." : undefined}
              >
                <Input
                  id="password"
                  type="password"
                  autoComplete={view.mode === "signup" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>
              <Button type="submit" busy={busy} className="w-full">
                {view.mode === "signup" ? "Create account and join" : "Sign in and join"}
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-muted">
              <button
                type="button"
                className="text-brand-600 underline"
                onClick={() => {
                  setError(null);
                  setView({ ...view, mode: view.mode === "signup" ? "signin" : "signup" });
                }}
              >
                {view.mode === "signup"
                  ? "I already have an account"
                  : "I need to create an account"}
              </button>
            </p>
          </Card>
        </>
      ) : null}
    </main>
  );
}
